/* ============================================================
   ui-primitives.jsx — small reusable bits
   ============================================================ */

import * as LucideIcons from 'lucide-react';

function toPascal(s) {
  return s.replace(/(^|-)([a-z])/g, (_m, _d, c) => c.toUpperCase());
}

// Icon wrapper — preserves the existing <Icon name="kebab-case" /> API by
// resolving the lucide-react component dynamically.
export function Icon({ name, size = 14, color, style, className }) {
  const key = toPascal(name);
  const Cmp = LucideIcons[key];
  if (!Cmp) {
    return (
      <span
        className={'lucide ' + (className || '')}
        style={{ display: 'inline-flex', width: size, height: size, lineHeight: 0, ...(style || {}) }}
      >
        <svg width={size} height={size} viewBox="0 0 24 24">
          <rect x="3" y="3" width="18" height="18" rx="2" fill="none" stroke="currentColor" strokeWidth="1.5" />
        </svg>
      </span>
    );
  }
  return (
    <Cmp
      size={size}
      color={color}
      strokeWidth={1.5}
      className={'lucide ' + (className || '')}
      style={{ display: 'inline-flex', ...(style || {}) }}
    />
  );
}

export function NumberField({ value, onChange, min, max, step = 0.1, unit = 'm', width = '100%' }) {
  return (
    <div style={{ display: 'flex', flex: 1, alignItems: 'center', gap: 4, width }}>
      <input
        type="number"
        className="field-input"
        value={value}
        min={min}
        max={max}
        step={step}
        onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
        style={{ flex: 1, textAlign: 'right' }}
      />
      <span style={{ color: 'var(--text-3)', fontFamily: 'var(--font-mono)', fontSize: 11 }}>{unit}</span>
    </div>
  );
}

export function Segment({ options, value, onChange }) {
  return (
    <div className="field-segment">
      {options.map((o) => (
        <button
          key={o.value}
          className={o.value === value ? 'is-active' : ''}
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function AngleViz({ angle, slantSign = 1, side = 'right', doubleSided = false, slotWidth = 2.5, slotLength = 5.0 }) {
  const W = 300, H = 80;
  const cx = W / 2, cy = H / 2;
  const θ = angle * Math.PI / 180;
  const w = slotWidth * 5, L = slotLength * 5;
  const step = angle <= 0.5 ? L : (w / Math.sin(θ));

  const numSlots = 6;
  const totalRowWidth = numSlots * step;
  const startX = cx - totalRowWidth / 2;

  function parallelogram(side, sg) {
    const polys = [];
    for (let i = 0; i < numSlots; i++) {
      const ax = startX + i * step;
      const ay = cy;
      const direction = sg;
      let p1, p2, p3, p4;
      if (angle <= 0.5) {
        p1 = [ax, ay];
        p2 = [ax + L, ay];
        p3 = [ax + L, ay + direction * w];
        p4 = [ax,     ay + direction * w];
      } else {
        const cos = Math.cos(θ);
        const sin = Math.sin(θ);
        p1 = [ax, ay];
        p2 = [ax + step, ay];
        p3 = [ax + step + slantSign * L * cos, ay + direction * L * sin];
        p4 = [ax + slantSign * L * cos,         ay + direction * L * sin];
      }
      polys.push(`${p1[0]},${p1[1]} ${p2[0]},${p2[1]} ${p3[0]},${p3[1]} ${p4[0]},${p4[1]}`);
    }
    return polys;
  }

  const polys = parallelogram(side, side === 'right' ? +1 : -1);
  const oppPolys = doubleSided
    ? parallelogram(side === 'right' ? 'left' : 'right', side === 'right' ? -1 : +1)
    : [];

  return (
    <div className="angle-viz">
      <svg className="angle-viz-svg" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid meet">
        <line x1={startX - 12} y1={cy} x2={startX + totalRowWidth + 12} y2={cy}
              stroke="#6ea8ff" strokeWidth="1.5" strokeDasharray="4 3" opacity="0.7" />
        {polys.map((p, i) => (
          <polygon key={i} points={p}
                   fill="rgba(190,167,142,0.35)"
                   stroke="#bea78e" strokeWidth="0.8" />
        ))}
        {oppPolys.map((p, i) => (
          <polygon key={'o'+i} points={p}
                   fill="rgba(190,167,142,0.22)"
                   stroke="#bea78e" strokeWidth="0.8" opacity="0.75" />
        ))}
        {angle > 0.5 && angle < 89.5 && (() => {
          const r = 10;
          const ax = startX + step;
          const ay = cy;
          const endX = ax + slantSign * Math.cos(θ) * r;
          const endY = ay + Math.sin(θ) * r;
          return (
            <g>
              <path d={`M ${ax + slantSign*r} ${ay} A ${r} ${r} 0 0 ${slantSign === 1 ? 0 : 1} ${endX} ${endY}`}
                    stroke="#f5d491" strokeWidth="1" fill="none" />
              <text x={ax + slantSign * 14} y={ay - 2}
                    fill="#f5d491" fontSize="9" fontFamily="JetBrains Mono"
                    textAnchor={slantSign === 1 ? 'start' : 'end'}>
                {Math.round(angle)}°
              </text>
            </g>
          );
        })()}
      </svg>
    </div>
  );
}

export function Swatch({ color, label }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <span style={{
        width: 10, height: 10, borderRadius: 2,
        background: color, border: '1px solid rgba(255,255,255,0.15)'
      }} />
      <span>{label}</span>
    </span>
  );
}

export function Toggle({ checked, onChange, label, hint }) {
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', userSelect: 'none' }}>
      <span style={{
        position: 'relative',
        width: 28, height: 16, borderRadius: 999,
        background: checked ? 'var(--sand)' : 'var(--panel-3)',
        transition: 'background 120ms',
        flexShrink: 0,
      }}>
        <span style={{
          position: 'absolute',
          top: 2, left: checked ? 14 : 2,
          width: 12, height: 12, borderRadius: '50%',
          background: '#fff',
          transition: 'left 120ms',
        }}/>
      </span>
      <span style={{ flex: 1 }}>
        <div style={{ fontSize: 12.5 }}>{label}</div>
        {hint && <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 1 }}>{hint}</div>}
      </span>
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} style={{ display: 'none' }} />
    </label>
  );
}
